/**
 * Trust Request detail view — timeline + child requests + actions.
 *
 * Route: /trust-requests/:request_id (institution surface)
 *
 * Shows:
 *   - status pill + type + purpose + timing
 *   - primary transition action (Send / Review / Complete / Cancel)
 *   - full timeline (append-only)
 *   - child requests (if this is a Trust Package parent)
 *   - linked evidence + audit references
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import api from "@/lib/api";
import {
  ArrowLeft, Send, CheckCircle2, XCircle, ClockIcon, ShieldCheck, ExternalLink,
  Circle, Package,
} from "lucide-react";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

const STATUS_TONE = {
  draft:               { label: "Draft",               bg: "#F1F5F9", color: "#334155" },
  sent:                { label: "Sent",                bg: "#DBEAFE", color: "#1E40AF" },
  consumer_accepted:   { label: "Accepted",            bg: "#E0E7FF", color: "#3730A3" },
  consumer_responding: { label: "Consumer Responding", bg: "#FEF3C7", color: "#92400E" },
  institution_review:  { label: "Institution Review",  bg: "#CCFBF1", color: "#0F766E" },
  completed:           { label: "Completed",           bg: "#D1FAE5", color: "#065F46" },
  declined:            { label: "Declined",            bg: "#FEE2E2", color: "#7F1D1D" },
  expired:             { label: "Expired",             bg: "#FEE2E2", color: "#7F1D1D" },
  cancelled:           { label: "Cancelled",           bg: "#F1F5F9", color: "#475569" },
  failed:              { label: "Failed",              bg: "#FEE2E2", color: "#7F1D1D" },
};

const TERMINAL = new Set(["completed", "declined", "expired", "cancelled", "failed"]);

export default function TrustRequestDetailPage() {
  const { request_id } = useParams();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/trust-requests/${request_id}`);
      setReq(data);
    } catch (e) {
      toast.error("Trust Request not found");
      navigate("/requests");
    } finally {
      setLoading(false);
    }
  }, [request_id, navigate]);

  useEffect(() => { load(); }, [load]);

  async function transitionTo(toStatus, message = "") {
    setBusy(true);
    try {
      await api.patch(`/trust-requests/${request_id}/status`,
        { to_status: toStatus, message });
      toast.success(`Status → ${STATUS_TONE[toStatus]?.label || toStatus}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transition failed");
    } finally {
      setBusy(false);
    }
  }

  async function complete() {
    setBusy(true);
    try {
      await api.post(`/trust-requests/${request_id}/complete`,
        { outcome_summary: "Completed via institution console" });
      toast.success("Trust Request completed. Evidence emitted.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Complete failed");
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    if (!window.confirm("Cancel this Trust Request?")) return;
    setBusy(true);
    try {
      await api.post(`/trust-requests/${request_id}/cancel`, { reason: "Cancelled from console" });
      toast.success("Cancelled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally {
      setBusy(false);
    }
  }

  async function executeViaUtility() {
    // Collect a minimum viable payload. Prefer the value stored on the
    // Trust Request metadata; otherwise prompt. For non-identity types
    // (learning, affordability) the mock is lenient — an empty payload
    // is fine.
    const identityCategories = new Set(["identity_verification", "kyc"]);
    let payload = {};
    if (identityCategories.has(req.request_type)) {
      const existing = req.metadata?.national_id;
      const nid = existing || window.prompt(
        "Enter the consumer's national ID to route through the Trust Utility:",
        "8001015009087",
      );
      if (!nid) return;
      payload = {
        national_id: String(nid).trim(),
        full_name: req.consumer_display_name || "",
        consumer_id: req.consumer_id || "",
      };
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/trust-utility/execute-for-request/${request_id}`,
        { payload },
      );
      toast.success(`Executed via ${data.provider_id} → ${data.status}`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Trust Utility execution failed";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  }

  if (loading || !req) {
    return <div className="p-8 text-sm text-slate-500">Loading Trust Request…</div>;
  }

  const tone = STATUS_TONE[req.status] || STATUS_TONE.draft;
  const isTerminal = TERMINAL.has(req.status);
  const isBundle = req.request_type === "trust_package";
  const utilityCompatible = new Set([
    "identity_verification", "kyc", "affordability",
    "product_understanding", "learning_assignment", "responsible_gaming_review",
  ]).has(req.request_type);
  const utilityExecutions = req.metadata?.trust_utility_executions || [];

  return (
    <div className="max-w-4xl mx-auto p-6 lg:p-10 space-y-6" data-testid="trust-request-detail">
      <Link to="/requests" data-testid="back-to-requests" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="w-4 h-4" /> Trust Requests
      </Link>

      {/* Header */}
      <header className="rounded-2xl bg-white border border-slate-100 shadow-sm p-6 space-y-3">
        <div className="flex items-start gap-3">
          {isBundle && (
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${GOLD}22`, color: GOLD }}>
              <Package className="w-5 h-5" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>
              {req.request_type.replace(/_/g, " ")} · {req.request_id}
            </div>
            <h1 className="font-display text-2xl font-semibold" style={{ color: NAVY }}>{req.purpose}</h1>
            {req.purpose_description && (
              <p className="text-[14px] text-slate-600 leading-relaxed mt-2">{req.purpose_description}</p>
            )}
          </div>
          <span
            data-testid="request-status-pill"
            className="text-[10px] uppercase tracking-widest font-mono px-2 py-1 rounded shrink-0"
            style={{ background: tone.bg, color: tone.color }}
          >
            {tone.label}
          </span>
        </div>

        {/* Meta */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 border-t border-slate-100 text-[12px]">
          <Meta label="Consumer" value={req.consumer_display_name || req.consumer_id || req.consumer_hint || "—"} />
          <Meta label="Created by" value={req.created_by} />
          <Meta label="Expires" value={req.expires_at ? format(new Date(req.expires_at), "d MMM yyyy") : "—"} />
        </div>

        {/* Actions */}
        {!isTerminal && (
          <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-100" data-testid="request-actions">
            {req.status === "sent" && (
              <ActionBtn testId="action-consumer_accepted" onClick={() => transitionTo("consumer_accepted", "Simulated consumer accept")} disabled={busy}>
                Simulate consumer accept
              </ActionBtn>
            )}
            {req.status === "consumer_accepted" && (
              <ActionBtn testId="action-consumer_responding" onClick={() => transitionTo("consumer_responding", "Consumer began responding")} disabled={busy}>
                Consumer started responding
              </ActionBtn>
            )}
            {req.status === "consumer_responding" && (
              <ActionBtn testId="action-institution_review" onClick={() => transitionTo("institution_review", "Ready for institution review")} disabled={busy}>
                Send to review
              </ActionBtn>
            )}
            <ActionBtn testId="action-complete" primary onClick={complete} disabled={busy}>
              <CheckCircle2 className="w-4 h-4" /> Complete
            </ActionBtn>
            <ActionBtn testId="action-cancel" tone="danger" onClick={cancel} disabled={busy}>
              <XCircle className="w-4 h-4" /> Cancel
            </ActionBtn>
          </div>
        )}
      </header>

      {/* Linked artefacts */}
      {(req.evidence_reference || req.trust_credential_reference) && (
        <section className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 space-y-2" data-testid="request-artefacts">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-emerald-900">
            Linked evidence
          </div>
          {req.evidence_reference && (
            <Link to={`/evidence?evidence_id=${req.evidence_reference}`} className="flex items-center gap-2 text-[13px] font-mono text-emerald-900">
              <ShieldCheck className="w-4 h-4" />
              {req.evidence_reference}
              <ExternalLink className="w-3 h-3" />
            </Link>
          )}
        </section>
      )}

      {/* Execute via Altara Trust Utility ──────────────── */}
      {utilityCompatible && (
        <section className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5 space-y-3" data-testid="request-trust-utility-panel">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
                Altara Trust Utility
              </div>
              <div className="text-[13px] text-slate-600 leading-relaxed max-w-2xl mt-1">
                Route this Trust Request through the provider-neutral orchestration layer.
                The active tenant preference (or the default) selects the vendor, results
                are normalised, and evidence is emitted automatically.
              </div>
            </div>
            <button
              data-testid="execute-via-trust-utility"
              onClick={executeViaUtility}
              disabled={busy || isTerminal}
              className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-[12px] font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: NAVY }}
            >
              <ShieldCheck className="w-4 h-4" /> Execute via Trust Utility
            </button>
          </div>
          {utilityExecutions.length > 0 && (
            <ol className="pt-3 border-t border-slate-100 space-y-1" data-testid="utility-executions">
              {utilityExecutions.map((ex) => (
                <li key={ex.execution_id} data-testid={`utility-exec-${ex.execution_id}`}
                    className="flex items-center gap-2 text-[12px]">
                  <span className="font-mono text-slate-400">{ex.execution_id}</span>
                  <span className="text-slate-500">·</span>
                  <span className="font-semibold" style={{ color: NAVY }}>{ex.provider}</span>
                  <span className="text-slate-500">→</span>
                  <span className={`font-semibold ${ex.status === "success" ? "text-emerald-700" : "text-red-700"}`}>{ex.status}</span>
                  {ex.evidence_reference && (
                    <>
                      <span className="text-slate-500">·</span>
                      <span className="font-mono text-slate-500">{ex.evidence_reference}</span>
                    </>
                  )}
                </li>
              ))}
            </ol>
          )}
        </section>
      )}

      {/* Children (Trust Package) */}
      {isBundle && (
        <section className="space-y-2" data-testid="request-children">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
            Bundled sub-requests ({req.children?.length || 0})
          </div>
          {(req.children || []).map((c) => {
            const cTone = STATUS_TONE[c.status] || STATUS_TONE.draft;
            return (
              <Link
                key={c.request_id}
                to={`/trust-requests/${c.request_id}`}
                data-testid={`child-row-${c.request_id}`}
                className="flex items-center gap-3 rounded-2xl bg-white border border-slate-100 p-3 hover:border-slate-300 hover:shadow-sm transition"
              >
                <Circle className="w-3 h-3 text-slate-300" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[13px]" style={{ color: NAVY }}>
                    {c.request_type.replace(/_/g, " ")}
                  </div>
                  <div className="text-[11px] text-slate-500">{c.purpose}</div>
                </div>
                <span
                  className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded shrink-0"
                  style={{ background: cTone.bg, color: cTone.color }}
                >
                  {cTone.label}
                </span>
              </Link>
            );
          })}
        </section>
      )}

      {/* Timeline */}
      <section data-testid="request-timeline" className="rounded-2xl bg-white border border-slate-100 p-5">
        <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-3">
          Timeline
        </div>
        <ol className="space-y-3">
          {(req.timeline || []).map((t) => {
            const to = t.to_status ? (STATUS_TONE[t.to_status]?.label || t.to_status) : null;
            return (
              <li key={t.entry_id} data-testid={`timeline-entry-${t.entry_id}`} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: "#F1F5F9" }}>
                  <ClockIcon className="w-3 h-3 text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px]" style={{ color: NAVY }}>
                    <strong>{to || t.kind}</strong>
                    {t.message ? <span className="text-slate-600"> — {t.message}</span> : null}
                  </div>
                  <div className="text-[11px] text-slate-400 font-mono mt-0.5">
                    {t.occurred_at ? format(new Date(t.occurred_at), "d MMM yyyy · HH:mm:ss") : ""}
                    {" · by "}{t.actor}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </section>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">{label}</div>
      <div className="text-[13px] font-semibold" style={{ color: NAVY }}>{value}</div>
    </div>
  );
}

function ActionBtn({ children, primary, tone, testId, ...rest }) {
  const style = primary
    ? "text-white"
    : tone === "danger"
      ? "text-red-700 bg-red-50 border border-red-200 hover:bg-red-100"
      : "text-slate-700 bg-white border border-slate-200 hover:bg-slate-50";
  const primaryStyle = primary ? { background: NAVY } : {};
  return (
    <button
      type="button"
      data-testid={testId}
      {...rest}
      className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-[12px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${style}`}
      style={primaryStyle}
    >
      {children}
    </button>
  );
}

// Minimal Send icon fallback if not needed elsewhere
export { Send };
